import { _t } from "@cruisen/i18n"
import { datesFormats, formatDate, formatBookedCruiseDates } from "./format"
import { CabinPricePP, ITripCollaborator } from "@cruisen/contract"

const displayNameForMonth = (month: Date) => {
  const shortenedMonthNameWithYear = formatDate(
    month.toISOString(),
    datesFormats.shortenedMonthNameWithYear,
  )
  return shortenedMonthNameWithYear
}

const areDatesContiguous = (dates: number[]) => {
  for (let i = 1; i < dates.length; i++) {
    const prevDate = new Date(dates[i - 1])
    const currentDate = new Date(dates[i])
    if (
      currentDate.getMonth() !== prevDate.getMonth() + 1 ||
      currentDate.getFullYear() !== prevDate.getFullYear()
    ) {
      return false
    }
  }
  return true
}

export const getTravelDatesText = (dates: number[]) => {
  if (!dates?.length) {
    return _t("tripDetails.anytime")
  }
  const firstMonth = new Date(dates[0])
  const firstMonthDisplayName = displayNameForMonth(firstMonth)

  if (dates.length === 1) return `${firstMonthDisplayName}`

  const lastMonth = new Date(dates[dates.length - 1])
  const lastMonthDisplayName = displayNameForMonth(lastMonth)

  if (areDatesContiguous(dates)) {
    return `${firstMonthDisplayName} - ${lastMonthDisplayName}`
  }

  if (dates.length === 2) return `${firstMonthDisplayName}, ${lastMonthDisplayName}`

  return `${firstMonthDisplayName} + ${dates.length - 1} ${_t("tripDetails.months")}`
}

export const getLocationsText = (locations: string[]) => {
  if (!locations || locations.length === 0) return _t("tripDetails.anywhere")
  if (locations.length === 1) return locations[0]
  return _t("tripDetails.locations", { amount: locations.length })
}

export const getSpecificLocationsText = ({
  groups,
  ports,
  isOrigin,
}: {
  groups: string[]
  ports: string[]
  isOrigin: boolean
}) => {
  // Check for individual port selections
  if (ports.length === 1) return ports[0]
  if (ports.length > 1) return _t("tripDetails.locations", { amount: ports.length })

  if (groups.length === 1) {
    if (isOrigin) return _t("tripDetails.anyportFrom", { group: groups[0] })
    return _t("tripDetails.anyportIn", { group: groups[0] })
  }
  if (groups.length > 1) {
    if (isOrigin) return _t("tripDetails.anyportFromLocations", { amount: groups.length })
    return _t("tripDetails.anyportInLocations", { amount: groups.length })
  }

  return _t("tripDetails.anyport")
}

export const getCabinScanRoomLayout = (pricePP: CabinPricePP) => {
  let roomLayout = ""

  if (pricePP.adults > 0) {
    roomLayout += pricePP.adults + " "
    roomLayout += pricePP.adults > 1 ? _t("roomLayout.adults") : _t("roomLayout.adult")

    if (pricePP.children > 0) roomLayout += ", "
  }

  if (pricePP.children > 0) {
    roomLayout += pricePP.children + " "
    roomLayout += pricePP.children > 1 ? _t("roomLayout.children") : _t("roomLayout.child")
  }
  return roomLayout
}

export const getTripDetails = (
  months: number[],
  collaborators: ITripCollaborator[],
  bookedCruiseDetails: {
    lineName: string
    startDate: string
    endDate: string
  },
) => {
  const result: string[] = []
  if (bookedCruiseDetails?.startDate && bookedCruiseDetails?.endDate) {
    const { startDate, endDate } = bookedCruiseDetails
    result.push(formatBookedCruiseDates(startDate, endDate))
  } else {
    result.push(getTravelDatesText(months))
  }
  // exclude agent collaborators
  const guests = collaborators?.filter(
    (collaborator) => !collaborator.type || collaborator.type === "user",
  )
  if (guests?.length > 1) {
    result.push(
      _t("tripListScreen.guests", {
        amount: guests.length,
      }),
    )
  }
  return result.join(" · ")
}
